import React, { useEffect, useMemo, useState } from 'react'
import './player-submit-form.styles.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import Modal, { ModalAlert } from '@/components/modal/modal.component';
import { sanitize } from 'isomorphic-dompurify';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import TextAreaWordCount from '@/components/SHARED/text-area-word-count/text-area-word-count.component';

const PlayerSubmitForm = ({ 
  elementData, 
  formData, 
  saveData, 
  type 
}) => {

  const screenSize = useSelector(state => state.contentData.screenSize);
  const [alertMessage, setAlertMessage] = useState(null);
  const [showUploadInfo, setShowUploadInfo] = useState(false);
  const hasConfiguredInputType = !!elementData?.opt_inputType;
  const inputType = elementData?.opt_inputType || 'shortAnswer';
  const mcOptions = useMemo(() => {
    if (!Array.isArray(elementData?.opt_inputOptions)) return [];
    return elementData.opt_inputOptions
      .map(o => (typeof o === 'string' ? o.trim() : ''))
      .filter(o => o.length > 0);
  }, [elementData?.opt_inputOptions]);
  const shouldShowLegacyInputs = !hasConfiguredInputType;
  const canUploadFile = shouldShowLegacyInputs || !!elementData?.opt_allowFileUpload;
  const location = useLocation();

  useEffect(() => {
    if (canUploadFile) return;
    saveData('file', null);
    saveData('fileName', null);
    saveData('fileType', null);
    saveData('upload', false);
  }, [canUploadFile])

  function clickUpload() {
    if (location.pathname.includes('/preview/player/')) {
      setAlertMessage('File uploads are not available in the game preview. ' + 
        'To test file uploads, you will need to publish your game and access ' + 
        'it through the normal play URL.');
      return;
    }
    document.getElementById('fileUpload').click();
  }

  function handleUpload(file) {
    if (!file) return;
    function cancelUpload() {
      document.getElementById('fileUpload').value = null;
      saveData('file', null);
      saveData('fileName', null);
      saveData('upload', false);
      // setFileName(null);
      return;
    }
    const ext = file.name.match(/\.([^\.]+)$/)[1].toLowerCase();
    console.log(ext);
    const allowed = ['png', 'jpg', 'webp', 'bmp', 'gif', 'tif', 'heif', 'psd', 'webm', 'mp4', 'mkv', 'mpg', 'ogg', 'mp3', 'm4a', 'flac', 'wav', 'wma', 'aac', 'pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'txt', 'zip', 'tar', 'rar', '7z']
    if (!allowed.includes(ext)) {
      setAlertMessage('That file type is not allowed to be uploaded. Try a different file.');
      cancelUpload();
      return;
    }
    console.log(file.size);
    if (file.size > 2 * 1024 * 1024) {
      setAlertMessage('That file is too large to be uploaded. You cannot upload files larger than 2MB.');
      cancelUpload();
      return;
    }
    saveData('file', file);
    saveData('fileName', file.name);
    saveData('fileType', file.type);
    saveData('upload', true);
  }

  return (
    <div className='player-submit-form'>
      {
        (shouldShowLegacyInputs || inputType === 'paragraph') &&
        <div className='field'>
          <label>{inputType === 'paragraph' ? 'Input your response here' : 'Input your text here'}<br /></label>
          <span className='meta'>(Required)</span>
          <TextAreaWordCount
            keyId='inputText'
            saveData={saveData}
            existingText={formData.inputText ?? ''}
            count={1000} />
        </div>
      }
      {
        (hasConfiguredInputType && inputType === 'shortAnswer') &&
        <div className='field'>
          <label>Input your response here:<br /></label>
          <span className='meta'>(Required)</span>
          <input
            type='text'
            value={formData.inputText ?? ''}
            onChange={(e) => saveData('inputText', sanitize(e.target.value.slice(0, 200)))}
          />
        </div> 
      }
      {
        (hasConfiguredInputType && inputType === 'multipleChoice') &&
        <div className='field'>
          <label>Choose an option</label>
          <span className='meta'>(Required)</span>
          {
            (mcOptions.length > 0)
              ?
              <div className='mc-options'>
                {
                  mcOptions.map((option, index) => (
                    <label className='mc-option' key={`player-mc-${index}`}>
                      <input
                        type='radio'
                        className='mc-input'
                        name='challenge-multiple-choice'
                        checked={formData.inputText === option}
                        onChange={() => {
                          saveData('inputText', option);
                          saveData('selectedOptionIndex', index);
                        }}
                      />
                      <span>{option}</span>
                    </label>
                  ))
                }
              </div>
              :
              <div className='meta'>This {type} has no options configured yet.</div>
          }
        </div>
      }
      {
        (hasConfiguredInputType && inputType === 'none') &&
        <div className='field'>
          <label>No text input required for this {type}.</label>
        </div>
      }
      {
        canUploadFile &&
        <div className='field'>
          <label className='with-info-icon'>Upload a File<FontAwesomeIcon icon={faInfoCircle} className='clickable' onClick={() => setShowUploadInfo(true)} /></label>
          <span className='meta'>(Optional)</span>
          <div className={`upload-div ${screenSize}`}>
            <input type="file" id="fileUpload" onChange={(e) => handleUpload(e.target.files[0])} />
            <button className={`g-button ${screenSize === 'mobile' ? 'small-btn' : ''}`} type="button" tabIndex="-1" onClick={() => clickUpload()}>Choose a File to Upload</button>
            <div>
              {
                formData.fileName ?
                  formData.fileName.length > 40 ?
                    formData.fileName.substring(0, 40) + '...' :
                    formData.fileName :
                  'No file chosen...'
              }
            </div>
          </div>
        </div>
      }
      <ModalAlert show={alertMessage} cancel={() => setAlertMessage(null)} message={alertMessage} />
      <Modal show={showUploadInfo && canUploadFile} cancel={() => setShowUploadInfo(false)} closeButton={true} cls='narrow' text='What kinds of files can be uploaded?'>
        <div className='modal-children'>
          <p>You can upload lots of different kinds of files as a response to a {type}. But remember that your file cannot be larger than <b>2Mb</b>. Here's what you can upload:</p>
          <ul>
            <li><b>Images</b> (png, jpg, webp, bmp, gif, tif, heif, psd)</li>
            <li><b>Videos</b> (webm, mp4, mkv, mpg, ogg)</li>
            <li><b>Audio Files</b> (mp3, m4a, flac, wav, wma, aac)</li>
            <li><b>Pdfs</b></li>
            <li><b>Documents</b> (doc, docx, xls, xlsx, csv, txt)</li>
            <li><b>Zip Files</b> (zip, tar, rar, 7z)</li>
          </ul>
          <p>If the file you need to submit is too large, or it isn't one of the above file types, you can always host the file in Google Drive or Dropbox or something similar and share the link in the text box.</p>
          <hr />
          <div className='buttons'>
            <button className='g-button' onClick={() => setShowUploadInfo(false)}>Ok</button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default PlayerSubmitForm;